"use server"

import { headers } from "next/headers"
import { ambilAuth, penyediaSosialAktif } from "@/modules/akun/service/auth"
import { ambilSesiAkun } from "@/modules/akun/service/sesi-akun"
import { catatAktivitasPermintaan } from "@/modules/akun/service/aktivitas"
import { verifikasiCaptcha } from "@/modules/captcha/service/captcha"
import { ambilIpPermintaan } from "@/shared/server/requester-info"
import { akunConfig, appConfig } from "@/shared/config/env"
import { rapikanTujuanLanjut } from "@/modules/akun/domain/tujuan-lanjut"
import { bacaGalatAuth, terjemahkanGalatAuth } from "@/modules/akun/domain/galat-auth"
import {
  PESAN_BATAS_PERCOBAAN,
  izinkanDuaLangkahPerAlamat,
  izinkanMasukPerAlamat,
  izinkanMasukPerIdentitas,
  izinkanPasswordBaruPerAlamat,
  izinkanResetPerAlamat,
  izinkanTautanPerAlamat,
  izinkanTautanPerEmail,
} from "@/modules/akun/domain/batas-laju-akun"
import { adalahEmail, emailAkunSchema, identitasMasukSchema, pesanPertama, validasiPasswordBaru } from "@/modules/akun/domain/validasi"
import { selesaikanMasukDuaLangkah } from "@/modules/akun/service/dua-langkah"
import { duaLangkahAktifUntukEmail } from "@/modules/akun/repository/dua-langkah-repo"
import type { ModeKodeDuaLangkah } from "@/modules/akun/domain/dua-langkah"
import type { HasilAksi, HasilData, HasilMasuk } from "@/modules/akun/domain/tipe"

const PESAN_SESI_BERAKHIR = "Sesi berakhir. Masuk lagi untuk melanjutkan."

function tujuanAman(lanjut: unknown): string | null {
  return rapikanTujuanLanjut(lanjut, [appConfig.appUrl, akunConfig.memberUrl])
}

export async function masukDenganPassword(masukan: { identitas: string; password: string; ingat: boolean; tokenCaptcha?: string }): Promise<HasilMasuk> {
  const identitas = identitasMasukSchema.safeParse(masukan.identitas)
  if (!identitas.success) return { ok: false, pesan: pesanPertama(identitas.error) }
  if (typeof masukan.password !== "string" || masukan.password.length === 0 || masukan.password.length > 128) {
    return { ok: false, pesan: "Isi password kamu" }
  }

  const ip = await ambilIpPermintaan()
  if (!izinkanMasukPerAlamat(ip) || !izinkanMasukPerIdentitas(identitas.data)) return { ok: false, pesan: PESAN_BATAS_PERCOBAAN }

  const captcha = await verifikasiCaptcha(masukan.tokenCaptcha)
  if (!captcha.valid) return { ok: false, pesan: captcha.error ?? "Selesaikan verifikasi keamanan dulu ya." }

  const auth = ambilAuth()
  const daftarHeader = await headers()

  try {
    const hasil = adalahEmail(identitas.data)
      ? await auth.api.signInEmail({ body: { email: identitas.data, password: masukan.password, rememberMe: masukan.ingat }, headers: daftarHeader })
      : await auth.api.signInUsername({ body: { username: identitas.data, password: masukan.password, rememberMe: masukan.ingat }, headers: daftarHeader })
    if (typeof hasil === "object" && hasil !== null && "twoFactorRedirect" in hasil && hasil.twoFactorRedirect) return { ok: true, duaLangkah: true }
    return { ok: true }
  } catch (error) {
    return { ok: false, pesan: terjemahkanGalatAuth(bacaGalatAuth(error), "Email, username, atau password salah.") }
  }
}

export async function verifikasiMasukDuaLangkah(masukan: { mode: ModeKodeDuaLangkah; kode: string; percayai: boolean }): Promise<HasilAksi> {
  const ip = await ambilIpPermintaan()
  if (!izinkanDuaLangkahPerAlamat(ip)) return { ok: false, pesan: PESAN_BATAS_PERCOBAAN }
  if (masukan.mode !== "totp" && masukan.mode !== "cadangan") return { ok: false, pesan: "Mode verifikasi tidak valid." }
  return selesaikanMasukDuaLangkah({ mode: masukan.mode, kode: masukan.kode, percayai: masukan.percayai === true })
}

export async function kirimTautanMasuk(masukan: { email: string; lanjut?: string | null; tokenCaptcha?: string }): Promise<HasilAksi> {
  const email = emailAkunSchema.safeParse(masukan.email)
  if (!email.success) return { ok: false, pesan: pesanPertama(email.error) }

  const ip = await ambilIpPermintaan()
  if (!izinkanTautanPerAlamat(ip) || !izinkanTautanPerEmail(email.data)) return { ok: false, pesan: PESAN_BATAS_PERCOBAAN }

  const captcha = await verifikasiCaptcha(masukan.tokenCaptcha)
  if (!captcha.valid) return { ok: false, pesan: captcha.error ?? "Selesaikan verifikasi keamanan dulu ya." }

  if (await duaLangkahAktifUntukEmail(email.data)) {
    return { ok: false, pesan: "Akun ini memakai verifikasi 2 langkah. Masuk dengan password atau akun Google/GitHub." }
  }

  const lanjut = tujuanAman(masukan.lanjut)
  const callbackVerifikasi = lanjut ? `/verifikasi?lanjut=${encodeURIComponent(lanjut)}` : "/verifikasi"

  try {
    await ambilAuth().api.signInMagicLink({
      body: { email: email.data, callbackURL: callbackVerifikasi, errorCallbackURL: "/masuk?galat=tautan" },
      headers: await headers(),
    })
    return { ok: true }
  } catch (error) {
    return { ok: false, pesan: terjemahkanGalatAuth(bacaGalatAuth(error), "Tautan belum bisa dikirim. Coba lagi sebentar.") }
  }
}

export async function mulaiMasukSosial(penyedia: "google" | "github", lanjutMentah?: string | null): Promise<HasilData<{ url: string }>> {
  if (!penyediaSosialAktif()[penyedia]) return { ok: false, pesan: "Metode masuk ini belum diaktifkan." }

  const lanjutSosial = tujuanAman(lanjutMentah)

  try {
    const hasil = await ambilAuth().api.signInSocial({
      body: { provider: penyedia, callbackURL: lanjutSosial ?? "/", newUserCallbackURL: lanjutSosial ? `/lengkapi?lanjut=${encodeURIComponent(lanjutSosial)}` : "/lengkapi", errorCallbackURL: "/masuk?galat=sosial" },
      headers: await headers(),
    })
    if (!hasil.url) return { ok: false, pesan: "Gagal membuka halaman masuk. Coba lagi sebentar." }
    return { ok: true, data: { url: hasil.url } }
  } catch (error) {
    return { ok: false, pesan: terjemahkanGalatAuth(bacaGalatAuth(error), "Gagal membuka halaman masuk. Coba lagi sebentar.") }
  }
}

export async function mintaResetPassword(masukan: { email: string; tokenCaptcha?: string }): Promise<HasilAksi> {
  const email = emailAkunSchema.safeParse(masukan.email)
  if (!email.success) return { ok: false, pesan: pesanPertama(email.error) }

  const ip = await ambilIpPermintaan()
  if (!izinkanResetPerAlamat(ip) || !izinkanTautanPerEmail(`reset:${email.data}`)) return { ok: false, pesan: PESAN_BATAS_PERCOBAAN }

  const captcha = await verifikasiCaptcha(masukan.tokenCaptcha)
  if (!captcha.valid) return { ok: false, pesan: captcha.error ?? "Selesaikan verifikasi keamanan dulu ya." }

  try {
    await ambilAuth().api.requestPasswordReset({
      body: { email: email.data, redirectTo: "/atur-ulang-password" },
      headers: await headers(),
    })
  } catch (error) {
    const galat = bacaGalatAuth(error)
    if (galat.status === 429) return { ok: false, pesan: terjemahkanGalatAuth(galat) }
    console.error("Gagal meminta atur ulang password:", error)
  }
  return { ok: true }
}

export async function aturUlangPassword(masukan: { token: string; password: string; konfirmasi: string }): Promise<HasilAksi> {
  const pesanPassword = validasiPasswordBaru(masukan.password, masukan.konfirmasi)
  if (pesanPassword) return { ok: false, pesan: pesanPassword }
  if (typeof masukan.token !== "string" || masukan.token.length === 0 || masukan.token.length > 256) {
    return { ok: false, pesan: "Tautan tidak valid atau sudah kedaluwarsa. Minta tautan baru." }
  }

  if (!izinkanPasswordBaruPerAlamat(await ambilIpPermintaan())) return { ok: false, pesan: PESAN_BATAS_PERCOBAAN }

  try {
    await ambilAuth().api.resetPassword({ body: { newPassword: masukan.password, token: masukan.token } })
    return { ok: true }
  } catch (error) {
    return { ok: false, pesan: terjemahkanGalatAuth(bacaGalatAuth(error), "Password belum bisa diubah. Minta tautan baru.") }
  }
}

export async function keluar(): Promise<HasilAksi> {
  const sesi = await ambilSesiAkun()
  if (!sesi) return { ok: false, pesan: PESAN_SESI_BERAKHIR }

  await catatAktivitasPermintaan(sesi.akunId, "keluar")
  try {
    await ambilAuth().api.signOut({ headers: await headers() })
    return { ok: true }
  } catch (error) {
    console.error("Gagal keluar:", error)
    return { ok: false, pesan: "Belum bisa keluar. Coba lagi sebentar." }
  }
}
